import { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";
// import { useSnackbar } from "notistack";
import { styled as muiStyled } from "@mui/material/styles";

import {
  Box,
  FormControl,
  FormControlLabel as MuiFormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import MuiLoadingButton from "@mui/lab/LoadingButton";

import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { IconToken } from "src/icons";

import {
  generateBasicContent,
  generateFieldSuggestions,
  generateHashtagSuggestions,
} from "src/api/generateAi";

import device from "src/styles/device";
import font from "src/styles/font";
import {
  Black,
  DarkGrey,
  Focused,
  Grey,
  GreyStroke,
  Primary,
  Red,
  White,
  White01,
} from "src/styles/color";
import { checkNotEmpty } from "src/common";
import { useAuth } from "src/hooks/useAuth";
import { useDialog } from "src/context/DialogContext";

import Button from "src/components/core/Button";
import CustomChip from "src/components/core/Chip";
import LabelSection from "src/components/core/LabelSection";
import { convertToBase64, imageUrlToBase64 } from "src/common/file";

const Dialog = muiStyled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogTitle-root": {
    borderBottom: `1px solid ${GreyStroke}`,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "650px",
    border: 0,
    padding: "24px",
    backgroundColor: White01,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 750,
      position: "relative",
    },
  },
  "& .MuiDialogActions-root": {
    borderTop: `1px solid ${GreyStroke}`,
    justifyContent: "space-between",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const FormControlLabel = muiStyled(MuiFormControlLabel)(({ theme }) => ({
  "&.MuiFormControlLabel-root": {
    width: "100%",
    margin: 0,
    padding: "2px 8px",
    paddingLeft: 0,
    border: `1px solid ${GreyStroke}`,
    borderRadius: 16,
  },

  [theme.breakpoints.down("sm")]: {
    "&.MuiFormControlLabel-root": {
      padding: "6px 12px",
    },
  },
}));

const LoadingButton = muiStyled(MuiLoadingButton, {
  shouldForwardProp: (prop) => prop !== "sx",
})((props) => ({
  borderRadius: 16,
  textTransform: "none",
  fontSize: "0.875rem",

  ":disabled": {
    cursor: "no-drop",
  },
  ":focus:not(:focus-visible)": {
    outline: "none",
    boxShadow: "none",
  },
  ":hover": {
    boxShadow: "none",
  },
  ":active": {
    boxShadow: "none",
  },
  ":focus": {
    outlineStyle: "solid",
    outlineWidth: "2px",
    outlineOffset: "0px",
    outlineColor: Focused,
    boxShadow: "none",
  },

  "& .MuiLoadingButton-loadingIndicatorStart": {
    left: 8,
  },

  "&.Mui-disabled": {
    cursor: "no-drop",
    pointerEvents: "auto",
  },

  "&.MuiButton-outlined": {
    border: `1px ${props.color === "secondary" ? GreyStroke : Primary} solid`,
    color: props.color === "secondary" ? Black : Primary,
    padding: "8px 16px",
    ":hover": {
      color: props.color === "secondary" ? "#ad46ff" : Red,
      backgroundColor: White01,
    },
    ":disabled": {
      border: `1px ${Grey} solid`,
      color: Grey,
      backgroundColor: White01,
      cursor: "no-drop",
    },
  },

  // custom
  "&.MuiButton-text": {
    color: DarkGrey,
    paddingLeft: 8,
    paddingRight: 8,
    ":active": {
      color: DarkGrey,
    },
    ":hover": {
      color: DarkGrey,
      backgroundColor: "transparent",
    },
    ":disabled": {
      backgroundColor: "transparent",
      color: Grey,
      cursor: "no-drop",
    },
  },

  "&.MuiButton-ai-suggest": {
    // backgroundColor: White,
    backgroundImage:
      "linear-gradient(90deg, oklch(62.3% 0.214 259.815) 0%, oklch(62.7% 0.265 303.9) 50%, oklch(65.6% 0.241 354.308) 100%)",
    color: White,
    padding: "8px 16px",
    fontWeight: 600,
    "& .MuiButton-startIcon": {
      marginRight: "4px",
    },
    "& .MuiButton-startIcon>*:nth-of-type(1)": {
      fontSize: "1rem",
    },
    ":hover": {
      opacity: 0.9,
    },
    ":disabled": {
      color: White01,
      cursor: "no-drop",
    },

    "&.MuiButton-sizeSmall": {
      padding: "4px 14px",
      fontSize: "0.75rem",
    },
  },
}));

const Chip = muiStyled(CustomChip)(({ theme }) => ({
  "&.MuiChip-root": {
    color: White,
    height: 22,
  },
  "&.MuiChip-filled": {
    "&.MuiChip-colorDefault": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
  },
  "& .MuiChip-label": { fontSize: font.size.sm, fontWeight: 500 },
}));

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 8px;
    right: 8px;
    color: ${Black};
  }
`;

// const AdBoxTitle = styled.div`
//   width: 100%;
//   /* height: max-content; */
//   display: flex;
// `;

const TokenBalanceWarning = styled.div`
  margin: 12px auto;
  padding: 8px 16px;
  width: max-content;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: #ca3500;
  background-color: #fff7ed;
  border: 1px solid #ffedd4;

  .MuiSvgIcon-root {
    color: #ff6900;
  }
  .MuiTypography-root {
    color: #ca3500;
  }
`;

const Wrapper = styled(Box)`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const WrapperSection = styled(Wrapper)`
  padding: 16px 24px;

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const WrapperEmpty = styled.div`
  width: 100%;
  min-height: 140px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const UlBlock = styled.ul`
  margin-top: 5px;
  margin-bottom: 0px;
  padding-inline-start: 28px;

  > li {
    margin: 4px 0;
    font-size: ${font.size.md};
    font-weight: 400;
    color: Black;

    span {
      font-size: ${font.size.md};
      font-weight: 600;
      line-height: 1.7;
      color: Black;
    }

    @media ${device.tablet} {
      font-size: ${font.size.sm};
    }
  }
`;

const FontP = styled.p`
  margin: 0;
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 1.7;
  color: ${(props) => (props.fontColor ? props.fontColor : Black)};

  span {
    font-size: ${font.size.md};
    font-weight: 600;
    line-height: 1.7;
    color: ${(props) => (props.fontColor ? props.fontColor : Black)};
  }

  &.text-indent {
    text-indent: 24px;

    @media ${device.mobileL} {
      text-indent: 16px;
    }
  }

  @media ${device.tablet} {
    font-size: ${font.size.sm} !important;
  }
`;

const GenerateLoading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .loading-icon {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SpanTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.lg};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 600)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.md};
  }
`;

const SpanItemName = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.md};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 500)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const SpanSubItem = styled.div`
  color: ${({ color }) => (color ? color : DarkGrey)};
  font-size: ${font.size.sm};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};
  margin-top: ${({ mt }) => (mt ? `${mt}px` : 0)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};
  text-align: ${({ textAlign }) => (textAlign ? textAlign : "left")};

  span {
    color: ${({ spanColor }) => (spanColor ? spanColor : Primary)};
    font-size: ${font.size.sm};
    font-weight: 500;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const ListHashtag = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItems = styled.button`
  padding: 4px 10px 4px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
  }

  ${({ isSelected }) =>
    isSelected
      ? css`
          border: 1px solid rgba(21, 93, 252, 0.7);
          background-color: #eff6ff;
          color: #155dfc;
        `
      : css`
          border: 1px solid ${GreyStroke};
          background-color: transparent;
          color: ${Black};
        `}
`;

const PreviewDesc = styled.div`
  width: 100%;
  display: block;
  white-space: pre-line;
  word-break: break-all;

  h3 {
    display: block;
    font-size: 1.25rem;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold !important;
  }
  p {
    word-break: break-all;
    font-size: 1rem;
    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
  }
`;

const WrapperPreview = styled.div`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  padding: 12px 16px;

  &:not(:last-child) {
    margin-bottom: 16px;
  }

  h3 {
    display: block;
    font-size: 1.25rem;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold !important;
  }
  h3:first-child {
    margin-block-start: 0;
  }
  p {
    word-break: break-all;
    font-size: 1rem;
    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
  }
`;

const GenerateSection = ({
  title,
  titleExtra = null,
  isLoading,
  hasGenerated,
  onRefresh,
  children,
}) => {
  return (
    <LabelSection
      title={title}
      style={{ marginBottom: 0 }}
      headerStyle={{ marginBottom: 12 }}
      titleExtra={titleExtra}
      actionMenu={() =>
        hasGenerated && (
          <LoadingButton
            variant="text"
            onClick={onRefresh}
            loading={isLoading}
            loadingPosition="start"
            startIcon={<AutorenewIcon />}
          >
            คิดใหม่
            <Chip
              variant="secondary"
              color="secondary"
              icon={<IconToken width={16} height={16} fill="currentColor" />}
              label={title === "แฮชแท็ก" ? "1" : "3"}
              sx={{
                ml: 1,
              }}
            />
          </LoadingButton>
        )
      }
    >
      {isLoading ? (
        <WrapperEmpty>
          <GenerateLoading>
            <AutorenewIcon className="loading-icon" />
            {`กำลังสร้าง${title}...`}
          </GenerateLoading>
        </WrapperEmpty>
      ) : !hasGenerated ? (
        <WrapperEmpty>
          <SpanItemName color={Grey}>
            {`ยังไม่มี${title}ที่ถูกสร้างขึ้น กดปุ่มด้านล่างเพื่อให้ AI สร้างให้`}
          </SpanItemName>

          <Button
            variant="ai-suggest"
            startIcon={<AutoFixHighIcon />}
            onClick={onRefresh}
          >
            {`สร้าง${title}`}
            <Chip
              icon={<IconToken width={16} height={16} fill="currentColor" />}
              label={title === "แฮชแท็ก" ? "1" : "3"}
              sx={{
                ml: 1,
              }}
            />
          </Button>
        </WrapperEmpty>
      ) : (
        children
      )}
    </LabelSection>
  );
};

const GenerateDetailDialog = ({
  dataUpload,
  dataUploadChoice,
  onSuccess,
  onClose,
  handleChange,
}) => {
  const { user, userTokenBalance, checkGenerateTokenBalance } = useAuth();
  const { openDialog } = useDialog();
  // const { enqueueSnackbar } = useSnackbar();

  // state for generating detail
  const [genName, setGenName] = useState(false);
  const [genHash, setGenHash] = useState(false);
  const [genDesc, setGenDesc] = useState(false);
  const [genAll, setGenAll] = useState(false);

  // Track whether each section has been generated at least once
  const [hasGeneratedName, setHasGeneratedName] = useState(false);
  const [hasGeneratedHash, setHasGeneratedHash] = useState(false);
  const [hasGeneratedDesc, setHasGeneratedDesc] = useState(false);

  const [productDetail, setProductDetail] = useState([
    {
      key: "item_name",
      name: "ชื่อสินค้า",
      value: "",
    },
    {
      key: "category",
      name: "หมวดหมู่",
      value: "",
    },
    {
      key: "product_types",
      name: "ประเภทสินค้า",
      value: "",
    },
    {
      key: "color",
      name: "สี",
      value: "",
    },
    {
      key: "pattern",
      name: "ลวดลาย",
      value: "",
    },
    {
      key: "texture",
      name: "ผิวสัมผัส",
      value: "",
    },
    {
      key: "recommend_rooms",
      name: "สถานที่ใช้งาน",
      value: "",
    },
    {
      key: "suitable_areas",
      name: "พื้นที่การใช้งาน",
      value: "",
    },
    {
      key: "product_features",
      name: "คุณสมบัติเฉพาะ",
      value: "",
    },
    {
      key: "size",
      name: "ขนาด",
      value: "",
    },
  ]);
  const [selectedData, setSelectedData] = useState({
    item_name: "",
    hashtags: [],
    description: "",
  });

  const [itemNameList, setItemNameList] = useState([]);
  const [hashtagsList, setHashtagsList] = useState([]);
  const [descriptionList, setDescriptionList] = useState([]);

  const isTokenEnough =
    user?.role_id === 3 ? userTokenBalance?.current_balance > 1 : true;
  const isHasBasicDetail =
    checkNotEmpty(dataUpload?.category) ||
    checkNotEmpty(dataUpload?.product_types) ||
    checkNotEmpty(dataUploadChoice?.[0]?.color) ||
    checkNotEmpty(dataUploadChoice?.[0]?.pattern);

  const isAnyGenerating = genName || genHash || genDesc || genAll;
  const hasAnyGenerated =
    hasGeneratedName || hasGeneratedHash || hasGeneratedDesc;

  const isFormValid = ["item_name", "hashtags", "description"].some(
    (required) => {
      let objVal = selectedData[required];
      return checkNotEmpty(objVal);
    },
  );

  useEffect(() => {
    if (!dataUpload || !dataUploadChoice) return;

    // console.log(dataUpload, "=============", dataUploadChoice);

    const formattedDetail = productDetail.map((data) => {
      let value = "";

      switch (data.key) {
        case "item_name":
          value = dataUpload?.item_name;
          break;
        case "category":
          value = dataUpload?.category?.cate_th;
          break;
        case "product_types":
          value =
            dataUpload?.product_types
              ?.map((type) => type.type_th)
              ?.join(", ") || "";
          break;
        case "color":
          value = dataUploadChoice?.[0]?.color?.color_th;
          break;
        case "pattern":
          value = dataUploadChoice?.[0]?.pattern?.pattern_th;
          break;
        case "texture":
          value = dataUploadChoice?.[0]?.texture_type?.type_th;
          break;
        case "recommend_rooms":
          value =
            dataUpload?.recommend_rooms
              ?.map((type) => type.room_th)
              ?.join(", ") || "";
          break;
        case "suitable_areas":
          value =
            dataUpload?.suitable_areas
              ?.map((type) => type.area_th)
              ?.join(", ") || "";
          break;
        case "product_features":
          value =
            dataUpload?.product_features
              ?.map((type) => type.feature_th)
              ?.join(", ") || "";
          break;
        case "size":
          value = dataUploadChoice[0]?.size?.width
            ? `${
                dataUploadChoice[0]?.size?.width
                  ? dataUploadChoice[0]?.size?.width
                  : ""
              } ${
                dataUploadChoice[0]?.size?.unit_width?.unit_th
                  ? dataUploadChoice[0]?.size?.unit_width?.unit_th
                  : ""
              } ${dataUploadChoice[0]?.size?.width ? "x" : ""} ${
                dataUploadChoice[0]?.size?.height
                  ? dataUploadChoice[0]?.size?.height
                  : ""
              } ${
                dataUploadChoice[0]?.size?.unit_height?.unit_th
                  ? dataUploadChoice[0]?.size?.unit_height?.unit_th
                  : ""
              } ${dataUploadChoice[0]?.size?.height ? "x" : ""} ${
                dataUploadChoice[0]?.size?.thickness
                  ? dataUploadChoice[0]?.size?.thickness
                  : "-"
              } ${
                dataUploadChoice[0]?.size?.unit_thickness?.unit_th
                  ? dataUploadChoice[0]?.size?.unit_thickness?.unit_th
                  : ""
              }`
            : `${dataUploadChoice[0]?.size?.capacity_size} ${
                dataUploadChoice[0]?.size?.unit_capacity?.unit_th
                  ? dataUploadChoice[0]?.size?.unit_capacity?.unit_th
                  : ""
              }` || "";
          break;
        default:
          break;
      }

      return {
        ...data,
        value: value,
      };
    });
    setProductDetail(formattedDetail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpload, dataUploadChoice]);

  /**
   * @notice This function is used to format the image list from the dataUploadChoice into base64 format for AI generation. It handles both URL strings and file objects, converting them to base64 as needed.
   * @return {Promise<Array<string>>} An array of base64-encoded image strings ready for AI processing.
   */
  const formatImageList = async () => {
    try {
      const imageUrls =
        Object.keys(dataUploadChoice?.[0]?.group_image)
          .filter((key) =>
            checkNotEmpty(dataUploadChoice?.[0]?.group_image?.[key]),
          )
          .map((key) => dataUploadChoice?.[0]?.group_image?.[key]) || [];

      return await Promise.all(
        imageUrls.map((image) => {
          if (typeof image === "string") {
            // if it's a URL, convert the image at the URL to base64
            return imageUrlToBase64(
              image
                .replace(
                  /devimage.xsurface.com/,
                  "devx-test-image.s3-ap-southeast-1.amazonaws.com",
                )
                .replace(
                  /productimage.xsurface.com/,
                  "product-xsurface.s3.ap-southeast-1.amazonaws.com",
                ),
            );
          }

          // if it's already a file object, convert it to base64
          return convertToBase64(image);
        }),
      );
    } catch (error) {
      console.error("Error formatting image list:", error);
    }
  };

  /**
   * @notice This function is used to generate item name suggestions based on the product details.
   */
  const generateItemName = useCallback(async () => {
    try {
      setGenName(true);

      const b64Images = await formatImageList();

      const genBody = {
        fieldName: "item_name",
        currentValue: dataUpload?.item_name || "",
        productName: dataUpload?.item_name || "",
        images: b64Images || [],
        context: productDetail.reduce((acc, item) => {
          return { ...acc, [item.key]: item.value };
        }, {}),
      };

      const resData = await generateFieldSuggestions(genBody);

      setItemNameList(resData.suggestions);
      setSelectedData((prev) => ({
        ...prev,
        item_name: "",
      })); // clear old selection when generating new item name
    } catch (error) {
      console.error("Error generating item name:", error);
    } finally {
      setGenName(false);
      setHasGeneratedName(true);
      if (user?.role_id === 3) checkGenerateTokenBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkGenerateTokenBalance, productDetail]);

  /**
   * @notice This function is used to generate hashtag suggestions based on the product details.
   */
  const generateHashtags = useCallback(async () => {
    try {
      setGenHash(true);

      const b64Images = await formatImageList();

      const genBody = {
        productName: dataUpload?.item_name || "",
        images: b64Images || [],
        context: productDetail.reduce((acc, item) => {
          return { ...acc, [item.key]: item.value };
        }, {}),
      };

      const resData = await generateHashtagSuggestions(genBody);

      setHashtagsList(resData.hashtags);
      setSelectedData((prev) => ({
        ...prev,
        hashtags: [],
      })); // clear old selection when generating new hashtags
      // setSelectedData((prev) => ({
      //   ...prev,
      //   hashtags: [...resData.hashtags],
      // })); // auto-select all
    } catch (error) {
      console.error("Error generating hashtags:", error);
    } finally {
      setGenHash(false);
      setHasGeneratedHash(true);
      if (user?.role_id === 3) checkGenerateTokenBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkGenerateTokenBalance, productDetail, dataUpload]);

  const generateDescription = useCallback(async () => {
    try {
      setGenDesc(true);

      const b64Images = await formatImageList();

      const genBody = {
        fieldName: "description",
        currentValue: dataUpload?.description || "",
        productName: dataUpload?.item_name || "",
        images: b64Images || [],
        context: productDetail.reduce((acc, item) => {
          return { ...acc, [item.key]: item.value };
        }, {}),
      };

      const resData = await generateFieldSuggestions(genBody);

      setDescriptionList(resData.suggestions);
      setSelectedData((prev) => ({
        ...prev,
        description: "",
      })); // clear old selection when generating new description
    } catch (error) {
      console.error("Error generating descriptions:", error);
    } finally {
      setGenDesc(false);
      setHasGeneratedDesc(true);
      if (user?.role_id === 3) checkGenerateTokenBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkGenerateTokenBalance, productDetail]);

  const generateAll = useCallback(async () => {
    try {
      setGenAll(true);
      const b64Images = await formatImageList();

      const genBody = {
        productName: dataUpload?.item_name || "",
        images: b64Images || [],
        context: productDetail.reduce((acc, item) => {
          return { ...acc, [item.key]: item.value };
        }, {}),
      };

      const resData = await generateBasicContent(genBody);

      if (!!resData) {
        setItemNameList([resData.item_name]);
        setHashtagsList([...resData.hashtags]);
        setDescriptionList([resData.description]);

        // Auto-select all generated content
        setSelectedData((prev) => ({
          ...prev,
          item_name: resData.item_name,
          hashtags: [...resData.hashtags],
          description: resData.description,
        }));

        setHasGeneratedName(true);
        setHasGeneratedHash(true);
        setHasGeneratedDesc(true);
      }
    } catch (error) {
      console.error("Error generating all details:", error);
    } finally {
      setGenAll(false);
      if (user?.role_id === 3) checkGenerateTokenBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkGenerateTokenBalance, dataUpload, dataUploadChoice, productDetail]);

  /**
   * @notice This function is used to toggle the selection of a hashtag. If the hashtag is already selected, it will be removed from the selection. If it is not selected, it will be added to the selection.
   * @param {string} tag - The hashtag to be toggled.
   */
  const toggleTag = (tag) => {
    setSelectedData((prev) => {
      const currentHashtags = prev.hashtags || [];
      const existingIndex = currentHashtags.findIndex(
        (hashtag) => hashtag.name === tag.name,
      );

      if (existingIndex !== -1) {
        // Remove tag if it exists
        const updatedHashtags = currentHashtags.filter(
          (_, index) => index !== existingIndex,
        );
        return { ...prev, hashtags: updatedHashtags };
      } else {
        // Add tag if it doesn't exist
        return { ...prev, hashtags: [...currentHashtags, { name: tag.name }] };
      }
    });
  };

  /**
   * @notice This function is used to select all hashtags. It will add all the hashtags in the hashtagsList to the selectedData state.
   */
  const selectAllTags = () => {
    setSelectedData((prev) => ({
      ...prev,
      hashtags: Array.from(hashtagsList),
    }));
  };

  /**
   * @notice This function is used to deselect all hashtags. It will remove all the hashtags from the selectedData state.
   */
  const deselectAllTags = () => {
    setSelectedData((prev) => ({ ...prev, hashtags: [] }));
  };

  const handleOnClose = () => {
    openDialog({
      type: "confirm",
      maxWidth: 350,
      title: "คุณแน่ใจหรือไม่ที่จะปิดหน้าต่างนี้?",
      onConfirm: () => {
        onClose();
      },
    });
  };

  const handleOnSubmit = async () => {
    openDialog({
      type: "confirm",
      maxWidth: 500,
      title: "ยืนยันข้อมูลที่เลือกตามรายการดังต่อไปนี้?",
      content: (
        <Box sx={{ textAlign: "left" }}>
          {checkNotEmpty(selectedData.item_name) && (
            <>
              <SpanItemName mb={8} color={DarkGrey}>
                ชื่อสินค้า:
              </SpanItemName>

              <WrapperPreview>
                <SpanItemName fontWeight={400}>
                  {selectedData.item_name}
                </SpanItemName>
              </WrapperPreview>
            </>
          )}

          {checkNotEmpty(selectedData.hashtags) && (
            <>
              <SpanItemName mb={8} color={DarkGrey}>
                แฮชแท็ก:
              </SpanItemName>

              <WrapperPreview>
                <SpanItemName fontWeight={400}>
                  {selectedData.hashtags
                    .map((tag) => `#${tag.name}`)
                    .join(", ")}
                </SpanItemName>
              </WrapperPreview>
            </>
          )}

          {checkNotEmpty(selectedData.description) && (
            <>
              <SpanItemName mb={8} color={DarkGrey}>
                คำอธิบาย:
              </SpanItemName>

              <WrapperPreview
                dangerouslySetInnerHTML={{
                  __html: selectedData.description,
                }}
              />
            </>
          )}

          <SpanSubItem color={Primary} fontWeight={500} textAlign="center">
            *หากยืนยันแล้ว
            ข้อมูลเหล่านี้จะแทนที่ข้อมูลเดิมในช่องกรอกข้อมูลสินค้า*
          </SpanSubItem>
        </Box>
      ),
      onConfirm: async () => {
        if (checkNotEmpty(selectedData.item_name))
          await handleChange(null, `item_name`, selectedData.item_name);

        if (checkNotEmpty(selectedData.hashtags))
          await handleChange(null, `choices.0.hashtags`, selectedData.hashtags);

        if (checkNotEmpty(selectedData.description))
          await handleChange(null, `description`, selectedData.description);

        await onSuccess();
      },
    });
  };

  return (
    <Dialog
      onClose={handleOnClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle>
        <SpanTitle>AI ช่วยกรอกข้อมูลสินค้า</SpanTitle>
        <SpanSubItem>
          ช่วยเพิ่มประสิทธิภาพด้วย <span>AI</span> และข้อมูลอ้างอิงจาก{" "}
          <span>XSURFACE</span>
        </SpanSubItem>

        {!isTokenEnough && (
          <TokenBalanceWarning>
            <ErrorOutlineIcon />
            <Typography variant="body2" ml={1} fontSize={font.size.sm}>
              คุณมีจำนวนเครดิตไม่เพียงพอต่อการใช้งาน
              กรุณาเติมเครดิตเพื่อใช้งานฟีเจอร์นี้
            </Typography>
          </TokenBalanceWarning>
        )}

        <CloseButton aria-label="close" onClick={handleOnClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent
        sx={{
          "&.MuiDialogContent-root": {
            pt: 2,
          },
          "::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {/* Product Detail */}
        <WrapperSection>
          {isHasBasicDetail ? (
            <>
              <SpanItemName>ข้อมูลสินค้าเบื้องต้น</SpanItemName>

              <UlBlock>
                {productDetail.map((detail, index) => {
                  if (checkNotEmpty(detail.value))
                    return (
                      <li key={`li-${index}`}>
                        <FontP>
                          <span>{detail.name}:</span> {detail.value}
                        </FontP>
                      </li>
                    );
                  return <></>;
                })}
              </UlBlock>
            </>
          ) : (
            <>
              <SpanItemName>ยังไม่ได้กรอกข้อมูล - AI จะสุ่มแนะนำ</SpanItemName>
              <SpanSubItem>
                การระบุข้อมูลเบื้องต้น เช่น หมวดหมู่ ประเภทสินค้า สี ลวดลาย
                จะช่วยให้ AI แนะนำได้แม่นยำขึ้น
              </SpanSubItem>
            </>
          )}
        </WrapperSection>

        {/* Item name */}
        <WrapperSection>
          <GenerateSection
            title="ชื่อสินค้า"
            isLoading={genName || genAll}
            hasGenerated={hasGeneratedName}
            onRefresh={generateItemName}
          >
            <FormControl>
              <RadioGroup
                name="select-item-name-radio-buttons-group"
                value={selectedData.item_name}
                sx={{
                  "&.MuiFormGroup-root": { gap: 1 },
                }}
                onChange={(e) => {
                  setSelectedData((prev) => ({
                    ...prev,
                    item_name: e.target.value,
                  }));
                }}
              >
                {itemNameList.map((name, index) => (
                  <FormControlLabel
                    key={`item-name-${index}`}
                    value={name}
                    control={
                      <Radio
                        sx={{
                          color: "#e04132",
                          "&.Mui-checked": {
                            color: "#e04132",
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: 22,
                          },
                        }}
                      />
                    }
                    label={name}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </GenerateSection>
        </WrapperSection>

        {/* Hashtags */}
        <WrapperSection>
          <GenerateSection
            title="แฮชแท็ก"
            isLoading={genHash || genAll}
            hasGenerated={hasGeneratedHash}
            onRefresh={generateHashtags}
            titleExtra={
              hashtagsList.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1} ml={1}>
                  <Button
                    variant="text"
                    sx={{ px: 1.5, py: 0.5, borderRadius: 2 }}
                    onClick={selectAllTags}
                  >
                    เลือกทั้งหมด
                  </Button>

                  <Divider orientation="vertical" variant="middle" flexItem />

                  <Button
                    variant="text"
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      "&.MuiButton-text": {
                        color: Grey,
                      },
                    }}
                    onClick={deselectAllTags}
                  >
                    ไม่เลือกทั้งหมด
                  </Button>
                </Stack>
              )
            }
          >
            <ListHashtag>
              {hashtagsList.map((tag, index) => {
                const isSelected = selectedData?.hashtags.some(
                  (hashtag) => hashtag.name === tag.name,
                );
                return (
                  <ListItems
                    key={`hashtag-${index}`}
                    isSelected={isSelected}
                    onClick={() => toggleTag(tag)}
                  >
                    {isSelected ? (
                      <CheckCircleOutlineOutlinedIcon
                        sx={{ color: "#155DFC", fontSize: font.size.lg }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{ color: DarkGrey, fontSize: font.size.lg }}
                      />
                    )}

                    <span>#{tag?.name}</span>
                  </ListItems>
                );
              })}
            </ListHashtag>
          </GenerateSection>
        </WrapperSection>

        {/* Description */}
        <WrapperSection>
          <GenerateSection
            title="คำอธิบายสินค้า"
            isLoading={genDesc || genAll}
            hasGenerated={hasGeneratedDesc}
            onRefresh={generateDescription}
          >
            <FormControl>
              <RadioGroup
                name="select-desc-radio-buttons-group"
                value={selectedData.description}
                sx={{
                  "&.MuiFormGroup-root": { gap: 1 },
                  "& .MuiFormControlLabel-root": {
                    alignItems: "flex-start",
                  },
                }}
                onChange={(e) => {
                  setSelectedData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }));
                }}
              >
                {descriptionList.map((desc, index) => (
                  <FormControlLabel
                    key={`desc-${index}`}
                    value={desc}
                    control={
                      <Radio
                        sx={{
                          color: "#e04132",
                          marginBlockStart: `1em`,
                          marginBlockEnd: "1em",
                          marginInlineStart: "0px",
                          marginInlineEnd: "0px",
                          "&.Mui-checked": {
                            color: "#e04132",
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: 22,
                          },
                        }}
                      />
                    }
                    label={
                      <PreviewDesc dangerouslySetInnerHTML={{ __html: desc }} />
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </GenerateSection>
        </WrapperSection>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {/* <IconToken width={16} height={16} fill={"#ad46ff"} />
          <Typography variant="body2" fontSize={font.size.sm}>
            การคิดแต่ละส่วนใช้ 1 เครดิต
          </Typography> */}
        </Stack>

        {!hasAnyGenerated ? (
          <LoadingButton
            variant="ai-suggest"
            onClick={() => generateAll()}
            loading={isAnyGenerating}
            loadingPosition="start"
            startIcon={<AutoFixHighIcon />}
            sx={{ maxWidth: 220 }}
          >
            สร้างทั้งหมด
            <Chip
              icon={<IconToken width={16} height={16} fill="currentColor" />}
              label="2"
              sx={{
                ml: 1,
              }}
            />
          </LoadingButton>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={() => generateAll()}
              loading={isAnyGenerating}
              loadingPosition="start"
              startIcon={<AutorenewIcon />}
            >
              คิดใหม่ทั้งหมด
              <Chip
                variant="secondary"
                color="secondary"
                icon={<IconToken width={16} height={16} fill="currentColor" />}
                label="2"
                sx={{
                  ml: 1,
                }}
              />
            </LoadingButton>

            <Button
              variant="contained"
              startIcon={<TaskAltIcon />}
              onClick={() => handleOnSubmit()}
              disabled={!isFormValid || isAnyGenerating || !isTokenEnough}
            >
              ใช้รายการที่เลือก
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateDetailDialog;
